import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  BRANCH_CHECK_ORDER_CREATION_ENDPOINT,
  datahubAxiosClient,
  ENDPOINT_DETAILS,
} from 'constants/server/apiEndPoint';
import notifyHandler from 'utils/notifyHandler';
import { BranchDetails, BranchProfile, SocialMedia } from './dto';
import { useLocalization } from 'localization';
import { BRANCH_PROFILE_ASSET } from 'constants/enums/asset';
import { SocialMediaPlatform } from 'constants/enums/general';

export const departmentQueryKey = {
  all: ['branches'] as const,
  packages: ['packages'] as const,
  checkDepartment: (id: string) => [...departmentQueryKey.all, id] as const,
  getPackages: (id: string) => [...departmentQueryKey.packages, id] as const,
  getBranch: (id: string) => [...departmentQueryKey.all, 'branchInformation', id] as const,
};

type BranchPayload = Partial<{
  file: File;
  type: BRANCH_PROFILE_ASSET;
  platform: SocialMediaPlatform;
  url: string;
}>;

export const checkBranchAllowedToCreateOrderApi = (branchId: string) => {
  const { data, isLoading, isFetching, isError } = useQuery<boolean>({
    queryKey: departmentQueryKey.checkDepartment(branchId),
    queryFn: async () => {
      const response = await datahubAxiosClient.post(BRANCH_CHECK_ORDER_CREATION_ENDPOINT, [branchId]);

      //If response[branchId] is returning false then the branchId should not be blocked and vice versa.
      return response?.data?.[branchId];
    },
    // TODO: onSuccess was removed in @tanstack/react-query v5 -- handle side effect via useEffect
    enabled: !!branchId,
    staleTime: 0,
  });

  return { data, isLoading, isFetching, isError };
};

export const useGetBranchInformationByIdApi = (branchId: string) => {
  const { data, isLoading, isFetching, isError } = useQuery<BranchDetails>({
    queryKey: departmentQueryKey.getBranch(branchId),
    queryFn: async () => {
      const response = await datahubAxiosClient.get(ENDPOINT_DETAILS.branchById(branchId));

      return response?.data;
    },
    staleTime: 0,
    enabled: !!branchId,
  });

  return { data, isLoading, isFetching, isError };
};

export const useUpdateBranchProfileMutation = (branchId: string) => {
  const { t } = useLocalization();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: BranchPayload) => {
      const { file, type } = payload;
      const form = new FormData();
      file && type && form.append(type, file);

      const { data } = await datahubAxiosClient.put<BranchProfile>(ENDPOINT_DETAILS.branchById(branchId), form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      return data;
    },
    onError: () => {
      notifyHandler({ type: 'error', content: t('common.terms.failedToUpload') });
    },
    onSuccess: () => {
      notifyHandler({ type: 'success', content: t('common.terms.uploadSuccess') });
      queryClient.invalidateQueries({ queryKey: departmentQueryKey.getBranch(branchId) });
    },
  });
};

export const useCreateBranchSocialMediaMutation = (branchId: string) => {
  const { t } = useLocalization();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: BranchPayload) => {
      const response = await datahubAxiosClient.post<SocialMedia>(ENDPOINT_DETAILS.createBranchSocialMedia(branchId), {
        platform: payload.platform,
        url: payload.url,
      });

      return response.data;
    },
    onError: () => {
      notifyHandler({ type: 'error', content: t('orders.pages.new.createOrderFailedDueToUnknownError') });
    },
    onSuccess: () => {
      notifyHandler({ type: 'success', content: t('common.terms.success') });
      queryClient.invalidateQueries({ queryKey: departmentQueryKey.getBranch(branchId) });
    },
  });
};

export const useUpdateBranchSocialMediaMutation = (branchId: string, socialMediaId: string) => {
  const { t } = useLocalization();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: BranchPayload) => {
      const response = await datahubAxiosClient.put<SocialMedia>(
        ENDPOINT_DETAILS.updateBranchSocialMedia(socialMediaId),
        {
          platform: payload.platform,
          url: payload.url,
        }
      );

      return response.data;
    },
    onError: () => {
      notifyHandler({ type: 'error', content: t('accountSetting.failedToUpdateSocialMedia') });
    },
    onSuccess: () => {
      notifyHandler({ type: 'success', content: t('common.terms.success') });
      queryClient.invalidateQueries({ queryKey: departmentQueryKey.getBranch(branchId) });
    },
  });
};

export const useDeleteBranchSocialMediaMutation = (branchId: string, socialMediaId: string) => {
  const { t } = useLocalization();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const response = await datahubAxiosClient.delete<SocialMedia>(
        ENDPOINT_DETAILS.updateBranchSocialMedia(socialMediaId)
      );

      return response.data;
    },
    onError: () => {
      notifyHandler({ type: 'error', content: t('accountSetting.failedToUpdateSocialMedia') });
    },
    onSuccess: () => {
      notifyHandler({ type: 'success', content: t('common.terms.success') });
      queryClient.invalidateQueries({ queryKey: departmentQueryKey.getBranch(branchId) });
    },
  });
};
